// client/src/components/event/ChallengeConfig.jsx
/**
 * =================================================================
 * Challenge Configuration Component
 * =================================================================
 * Follows AuctionConfig pattern for consistency.
 * FEATURES:
 * 1. Set hashtag, prize details, number of winners
 * 2. Schedule start/end date and time
 * 3. Display shareable hyperlink
 * 4. View entries and statistics
 * 5. Manual winner selection
 * =================================================================
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import "./AuctionConfig.css";

const ChallengeConfig = ({ tenantId, eventId, itemId, itemName, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const [config, setConfig] = useState({
    name: itemName || "Challenge",
    hashtag: "",
    description: "",
    prize_details: "",
    winners_to_pick: 1,
    start_at: "",
    end_at: "",
    status: "NEW",
  });

  const [entries, setEntries] = useState([]);
  const [winners, setWinners] = useState([]);
  const [showEntries, setShowEntries] = useState(false);
  const [showWinners, setShowWinners] = useState(false);
  const [entryCount, setEntryCount] = useState(0);
  const [challengeExists, setChallengeExists] = useState(false);

  const baseUrl = process.env.REACT_APP_BASE_URL || "";
  const shareLink = challengeExists
    ? `${window.location.origin}/${tenantId}/${eventId}/${itemId}/action?type=challenge`
    : "";

  useEffect(() => {
    loadConfig();
  }, [tenantId, eventId, itemId]);

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return "";
    }
  };

  const formatDateTimeForBackend = (val) => {
    if (!val) return null;
    try {
      const d = new Date(val);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    } catch {
      return null;
    }
  };

  const loadConfig = async () => {
    setLoading(true);
    setMessage({ text: "", type: "" });

    try {
      const response = await axios.get(
        `${baseUrl}/api/challenge/${tenantId}/event/${eventId}/item/${itemId}/config`
      );

      if (response.data.success && response.data.challenge) {
        const c = response.data.challenge;
        setChallengeExists(true);
        setConfig({
          name: c.name || itemName || "Challenge",
          hashtag: c.hashtag || "",
          description: c.description || "",
          prize_details: c.prize_details || "",
          winners_to_pick: c.winners_to_pick || 1,
          start_at: formatDateForInput(c.start_at),
          end_at: formatDateForInput(c.end_at),
          status: c.status || "NEW",
        });

        if (c.status === "COMPLETE") {
          loadWinners();
        }

        // Load entry count
        loadEntryCount();
      } else {
        setChallengeExists(false);
        setMessage({
          text: "No challenge configured yet. Fill out the form below to create one.",
          type: "info",
        });
      }
    } catch (err) {
      console.error("Error loading challenge config:", err);
      if (err.response?.status === 404) {
        setMessage({
          text: "No challenge configured yet. Fill out the form below to create one.",
          type: "info",
        });
      } else {
        setMessage({ text: "Failed to load challenge configuration", type: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const loadEntryCount = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/challenge/${tenantId}/event/${eventId}/item/${itemId}/entries`
      );
      if (response.data.success) {
        setEntryCount(response.data.count || response.data.entries?.length || 0);
      }
    } catch {
      // Non-critical
    }
  };

  const loadEntries = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/challenge/${tenantId}/event/${eventId}/item/${itemId}/entries`
      );
      if (response.data.success) {
        setEntries(response.data.entries);
        setEntryCount(response.data.count || response.data.entries.length);
        setShowEntries(true);
      }
    } catch (err) {
      console.error("Error loading entries:", err);
      setMessage({ text: "Failed to load entries", type: "error" });
    }
  };

  const loadWinners = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/challenge/${tenantId}/event/${eventId}/item/${itemId}/winners`
      );
      if (response.data.success) {
        setWinners(response.data.winners);
        setShowWinners(true);
      }
    } catch (err) {
      console.error("Error loading winners:", err);
      setMessage({ text: "Failed to load winners", type: "error" });
    }
  };

  const handleInputChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const validateConfig = () => {
    const errors = [];
    if (!config.name || config.name.trim().length === 0) {
      errors.push("Challenge name is required");
    }
    if (config.winners_to_pick < 1 || config.winners_to_pick > 100) {
      errors.push("Winners to pick must be between 1 and 100");
    }
    if (config.start_at && config.end_at) {
      if (new Date(config.end_at) <= new Date(config.start_at)) {
        errors.push("End date must be after start date");
      }
    }
    return errors;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const errors = validateConfig();
    if (errors.length > 0) {
      setMessage({ text: errors.join(". "), type: "error" });
      return;
    }

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const payload = {
        name: config.name,
        hashtag: config.hashtag || null,
        description: config.description || null,
        prize_details: config.prize_details || null,
        winners_to_pick: Number(config.winners_to_pick),
        start_at: formatDateTimeForBackend(config.start_at),
        end_at: formatDateTimeForBackend(config.end_at),
        status: config.status,
      };

      const response = await axios.post(
        `${baseUrl}/api/challenge/${tenantId}/event/${eventId}/item/${itemId}/config`,
        payload
      );

      if (response.data.success) {
        setChallengeExists(true);
        setMessage({ text: "Challenge configuration saved successfully!", type: "success" });
        setTimeout(() => {
          setMessage({ text: "", type: "" });
          loadConfig();
        }, 2000);
      }
    } catch (err) {
      console.error("Error saving challenge config:", err);
      setMessage({
        text: err.response?.data?.message || "Failed to save challenge configuration",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const copyShareLink = () => {
    if (!shareLink) return;
    navigator.clipboard.writeText(shareLink).then(
      () => {
        setMessage({ text: "Link copied to clipboard!", type: "success" });
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      },
      () => setMessage({ text: "Failed to copy link", type: "error" })
    );
  };

  const handleAwardWinners = async () => {
    if (!window.confirm("Award winners now? This will randomly select winners from the entries.")) {
      return;
    }

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const response = await axios.post(
        `${baseUrl}/api/challenge/${tenantId}/event/${eventId}/item/${itemId}/award`
      );
      if (response.data.success) {
        setMessage({
          text: `${response.data.winners?.length || 0} winner(s) selected!`,
          type: "success",
        });
        setTimeout(() => {
          loadConfig();
          loadWinners();
        }, 1000);
      }
    } catch (err) {
      console.error("Error awarding winners:", err);
      setMessage({
        text: err.response?.data?.message || "Failed to award winners",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this challenge? This cannot be undone.")) return;

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const response = await axios.delete(
        `${baseUrl}/api/challenge/${tenantId}/event/${eventId}/item/${itemId}`
      );
      if (response.data.success) {
        setMessage({ text: "Challenge deleted successfully", type: "success" });
        setTimeout(() => {
          if (onClose) onClose();
        }, 1500);
      }
    } catch (err) {
      console.error("Error deleting challenge:", err);
      setMessage({
        text: err.response?.data?.message || "Failed to delete challenge",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="auction-config-container">
        <div className="loading">Loading challenge configuration...</div>
      </div>
    );
  }

  const isComplete = config.status === "COMPLETE";

  return (
    <div className="auction-config-container">
      {/* Header */}
      <div className="auction-config-header">
        <h2>Challenge Configuration</h2>
        <button className="close-button" onClick={onClose} aria-label="Close">
          &times;
        </button>
      </div>

      {/* Messages */}
      {message.text && (
        <div className={`message message-${message.type}`}>{message.text}</div>
      )}

      {/* Share Link */}
      {shareLink && (
        <div className="share-link-section">
          <h3>Shareable Link</h3>
          <p className="share-link-description">
            Share this link with participants to submit their challenge entries:
          </p>
          <div className="share-link-container">
            <div className="share-link-display">
              <a href={shareLink} target="_blank" rel="noopener noreferrer">
                {shareLink}
              </a>
            </div>
            <button type="button" className="copy-link-button" onClick={copyShareLink}>
              Copy Link
            </button>
          </div>
        </div>
      )}

      {/* Statistics */}
      {challengeExists && (
        <div className="auction-stats-section">
          <h3>Challenge Statistics</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-label">Total Entries</div>
              <div className="stat-value">{entryCount}</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Winners to Pick</div>
              <div className="stat-value">{config.winners_to_pick}</div>
            </div>
          </div>
          <div className="stats-actions">
            <button type="button" className="view-entries-button" onClick={loadEntries}>
              View All Entries
            </button>
            {isComplete && (
              <button type="button" className="view-winners-button" onClick={loadWinners}>
                View Winners
              </button>
            )}
          </div>
        </div>
      )}

      {/* Configuration Form */}
      <form className="auction-config-form" onSubmit={handleSave}>
        <div className="form-group">
          <label htmlFor="name">
            Challenge Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            value={config.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            placeholder="Enter challenge name"
            disabled={isComplete}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="hashtag">Hashtag</label>
          <input
            type="text"
            id="hashtag"
            value={config.hashtag}
            onChange={(e) => handleInputChange("hashtag", e.target.value)}
            placeholder="#YourChallengeHashtag"
            disabled={isComplete}
          />
          <span className="form-hint">
            Participants will use this hashtag when sharing on social media
          </span>
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            value={config.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            placeholder="Describe the challenge rules and requirements"
            disabled={isComplete}
            rows={3}
          />
        </div>

        <div className="form-group">
          <label htmlFor="prize_details">Prize Details</label>
          <textarea
            id="prize_details"
            value={config.prize_details}
            onChange={(e) => handleInputChange("prize_details", e.target.value)}
            placeholder="Describe the prizes for winners"
            disabled={isComplete}
            rows={2}
          />
        </div>

        <div className="form-group">
          <label htmlFor="winners_to_pick">
            Number of Winners <span className="required">*</span>
          </label>
          <input
            type="number"
            id="winners_to_pick"
            min="1"
            max="100"
            value={config.winners_to_pick}
            onChange={(e) => handleInputChange("winners_to_pick", parseInt(e.target.value) || 1)}
            disabled={isComplete}
            required
          />
          <span className="form-hint">Number of winners to randomly select</span>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="start_at">Start Date & Time</label>
            <input
              type="datetime-local"
              id="start_at"
              value={config.start_at}
              onChange={(e) => handleInputChange("start_at", e.target.value)}
              disabled={isComplete}
            />
            <span className="form-hint">When the challenge opens</span>
          </div>
          <div className="form-group">
            <label htmlFor="end_at">End Date & Time</label>
            <input
              type="datetime-local"
              id="end_at"
              value={config.end_at}
              onChange={(e) => handleInputChange("end_at", e.target.value)}
              disabled={isComplete}
            />
            <span className="form-hint">When the challenge closes</span>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="status">Status</label>
          <select
            id="status"
            value={config.status}
            onChange={(e) => handleInputChange("status", e.target.value)}
            disabled={isComplete}
          >
            <option value="NEW">New</option>
            <option value="ACTIVE">Active</option>
            <option value="PROCESSING">Processing</option>
            <option value="COMPLETE">Complete</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>

        <div className="form-actions">
          {isComplete ? (
            <div className="completion-notice">
              Challenge is complete - no further changes allowed
            </div>
          ) : (
            <>
              <button type="submit" className="save-button" disabled={saving}>
                {saving ? "Saving..." : "Save Configuration"}
              </button>
              {challengeExists && entryCount > 0 && (
                <button
                  type="button"
                  className="award-button"
                  onClick={handleAwardWinners}
                  disabled={saving}
                >
                  Award Winners
                </button>
              )}
              {challengeExists && (
                <button
                  type="button"
                  className="delete-button"
                  onClick={handleDelete}
                  disabled={saving}
                >
                  Delete Challenge
                </button>
              )}
            </>
          )}
        </div>
      </form>

      {/* Entries Modal */}
      {showEntries && (
        <div className="modal-overlay" onClick={() => setShowEntries(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>All Entries ({entries.length})</h3>
              <button className="close-button" onClick={() => setShowEntries(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <table className="entries-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Submission</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry, index) => (
                    <tr key={entry.id}>
                      <td>{index + 1}</td>
                      <td>{entry.name}</td>
                      <td>{entry.email}</td>
                      <td>
                        {entry.submission_url ? (
                          <a href={entry.submission_url} target="_blank" rel="noopener noreferrer">
                            View
                          </a>
                        ) : (
                          entry.submission_text || "—"
                        )}
                      </td>
                      <td>{new Date(entry.created_on).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Winners Modal */}
      {showWinners && (
        <div className="modal-overlay" onClick={() => setShowWinners(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Winners ({winners.length})</h3>
              <button className="close-button" onClick={() => setShowWinners(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <table className="winners-table">
                <thead>
                  <tr>
                    <th>Place</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Submission</th>
                  </tr>
                </thead>
                <tbody>
                  {winners.map((winner, index) => (
                    <tr key={winner.id} className="winner-row">
                      <td className="place-cell">
                        {index === 0 ? "1st" : index === 1 ? "2nd" : index === 2 ? "3rd" : `${index + 1}th`}
                      </td>
                      <td>{winner.name}</td>
                      <td>{winner.email}</td>
                      <td>
                        {winner.submission_url ? (
                          <a href={winner.submission_url} target="_blank" rel="noopener noreferrer">
                            View
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChallengeConfig;
